import React, { Fragment, useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { includes, map } from "ramda";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";

import CheckIcon from "@mui/icons-material/Check";
import { notBlank } from "../util/ext";
import { useMediaTable } from "../creator-application/common/use-mediaTable";
import Tooltip from "@mui/material/Tooltip";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import OpenIcon from "@mui/icons-material/Launch";
import ListSummaryCard from "../creator-application/common/ListSummaryCard";
import { Link, useResolvedPath } from "react-router-dom";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },

  cell: {
    minWidth: 80,
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const OrganizationTable = ({
  organizationList,
  page,
  onPageChange,
  onSelectChange,
  orderBy = "organizationName",
  order = "asc",
  onSortChange,
  onScrollVisible,
}) => {
  const classes = useStyles();
  const url = useResolvedPath(".").pathname;
  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Organizations">
          <TableHead>
            <TableRow>
              <TableCell key="name" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "organizationName"}
                  direction={order}
                  onClick={() => handleRequestSort("organizationName")}
                >
                  Name
                  {orderBy === "organizationName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="locality" align="left" className={classes.cell}>
                Locality
              </TableCell>

              <TableCell key="alias" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "alias"}
                  direction={order}
                  onClick={() => handleRequestSort("alias")}
                >
                  Alias
                  {orderBy === "alias" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="active" align="center" className={classes.cell}>
                Active
              </TableCell>

              <TableCell
                key="buying-group"
                align="center"
                className={classes.cell}
              >
                Is Buying Group
              </TableCell>

              <TableCell key="library" align="center" className={classes.cell}>
                Is Library
              </TableCell>

              <TableCell
                key="buying-groups"
                align="left"
                className={classes.cell}
              >
                Buying Groups
              </TableCell>

              <TableCell
                key="date-updated"
                align="left"
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "dateUpdated"}
                  direction={order}
                  onClick={() => handleRequestSort("dateUpdated")}
                >
                  Last Update
                  {orderBy === "dateUpdated" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="updated-by" align="left" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "updatedBy"}
                  direction={order}
                  onClick={() => handleRequestSort("updatedBy")}
                >
                  Updated By
                  {orderBy === "updatedBy" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="actions" align="center" style={{ width: 100 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {organizationList.map(org => (
              <TableRow
                onClick={() => handleRowClick(org)}
                hover
                tabIndex={-1}
                key={org.organizationId}
                selected={includes(org.organizationId, state.selected)}
              >
                <TableCell
                  key={"name:" + org.organizationId}
                  align="left"
                  className={classes.cell}
                >
                  <Box>
                    <Box>{org.organizationName}</Box>
                    <Box fontSize="1.2rem">({org.organizationId})</Box>
                  </Box>
                </TableCell>

                <TableCell key={"locality: " + org.organizationId}>
                  {org?.location ? (
                    <Box>
                      <Box>{org.location?.locality}</Box>
                      <Box>
                        {org.location?.administrativeArea}{" "}
                        {org.location?.country}
                      </Box>
                    </Box>
                  ) : (
                    "-"
                  )}
                </TableCell>

                <TableCell
                  key={"alias:" + org.organizationId}
                  align="left"
                  className={classes.cell}
                >
                  {org.alias}
                </TableCell>

                <TableCell
                  key={"active:" + org.organizationId}
                  align="center"
                  className={classes.cell}
                >
                  {org.active && (
                    <Fragment>
                      <CheckIcon fontSize="large" color="primary" />
                      <Typography className={classes.visuallyHidden}>
                        Edit Account
                      </Typography>
                    </Fragment>
                  )}
                </TableCell>

                <TableCell
                  key={"buying-group:" + org.organizationId}
                  align="center"
                  className={classes.cell}
                >
                  {org.buyingGroup && (
                    <Fragment>
                      <CheckIcon fontSize="large" color="primary" />
                      <Typography className={classes.visuallyHidden}>
                        is buying group
                      </Typography>
                    </Fragment>
                  )}
                </TableCell>

                <TableCell
                  key={"library:" + org.organizationId}
                  align="center"
                  className={classes.cell}
                >
                  {org.library && (
                    <Fragment>
                      <CheckIcon fontSize="large" color="primary" />
                      <Typography className={classes.visuallyHidden}>
                        is library
                      </Typography>
                    </Fragment>
                  )}
                </TableCell>

                <TableCell
                  key={"buying-groups:" + org.organizationId}
                  align="left"
                  className={classes.cell}
                >
                  <ListSummaryCard
                    data={map(
                      bg => ({
                        id: bg.organizationId,
                        name: bg.organizationName,
                      }),
                      org.buyingGroups,
                    )}
                  />
                </TableCell>

                <TableCell
                  key={"date-updated:" + org.organizationId}
                  align="left"
                  className={classes.cell}
                >
                  {new Date(org.dateUpdated).toLocaleDateString()}
                </TableCell>

                <TableCell
                  key={"updated-by:" + org.organizationId}
                  align="left"
                  className={classes.cell}
                >
                  {org.updatedByUsername}
                </TableCell>

                <TableCell
                  key={"actions:" + org.organizationId}
                  align="center"
                  style={{ width: 100 }}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Tooltip title="View">
                      <Link to={`${url}/${org.organizationId}`}>
                        <OpenIcon fontSize="large" color="primary" />
                      </Link>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[10]}
        component="div"
        count={-1}
        rowsPerPage={10}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
        nextIconButtonProps={{
          disabled: organizationList.length < 10,
        }}
        labelDisplayedRows={({ page }) => `Page ${page + 1}`}
      />
    </Paper>
  );
};

export default OrganizationTable;
